export const ONE_MINUTE_MS = 60_000;

/**
 * '1 minute' is one point per READING, and readings do not sit on a minute grid: a 60 s
 * cadence with a few seconds of jitter puts two readings in one minute and none in the next.
 * Gridding that would break the line every hour or so over nothing. So at this grain a gap is
 * a silence longer than three readings, and only that gets a `null`.
 */
export function breakOnSilence(
  parsed: ReadonlyArray<[number, number | null]>,
  silenceMs = 3 * ONE_MINUTE_MS,
): Array<[number, number | null]> {
  const out: Array<[number, number | null]> = [];
  let prev: number | null = null;
  for (const [t, v] of parsed) {
    if (prev !== null && t - prev > silenceMs) out.push([prev + ONE_MINUTE_MS, null]);
    out.push([t, v]);
    prev = t;
  }
  return out;
}

/**
 * Bucketed rows with a gap row wherever a bucket went unread (2026-10-20: the bank quick views drew
 * 19 Oct 4 PM straight across to 20 Oct 7 AM while the local stack slept). A range read returns only
 * the buckets that HAD readings, so a silence is an absence, not a null, and `connectNulls: false`
 * breaks only on a null it can see. Two neighbouring buckets are `bucketMs` apart; any wider step gets
 * one gap row at the first bucket that is missing. `gap` makes that row in the caller's shape — a
 * `[t, null]` pair, a `[t, null, null]` range, a stacked row with every part null.
 */
export function breakAtGaps<R>(rows: readonly R[], tOf: (r: R) => number, bucketMs: number, gap: (t: number) => R): R[] {
  const out: R[] = [];
  let prev: number | null = null;
  for (const r of [...rows].sort((a, b) => tOf(a) - tOf(b))) {
    const t = tOf(r);
    if (prev !== null && t - prev > bucketMs) out.push(gap(prev + bucketMs));
    out.push(r);
    prev = t;
  }
  return out;
}
